/**
 * The frozen halves of an invoice.
 *
 * These shapes are written into `sales.invoices` as JSON and then never
 * touched. They are typed here rather than inferred so that a later change to
 * a Store or Organization column cannot silently change what an invoice prints
 * — which is the entire reason the snapshot exists.
 */

export interface PartySnapshot {
  /** The name that appears on the document. */
  name: string;
  addressLines: string[];
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  gstin: string | null;
  email: string | null;
  phone: string | null;
}

export interface SellerSnapshot extends PartySnapshot {
  /** Data URI or absolute URL, resolved at render time and stored as given. */
  logoUrl: string | null;
  bank: BankSnapshot | null;
}

export interface BankSnapshot {
  bankName: string | null;
  accountNumber: string | null;
  ifsc: string | null;
  accountName: string | null;
}

/** A blank party, so a missing record never produces `undefined` on paper. */
export function emptyParty(name: string): PartySnapshot {
  return {
    name,
    addressLines: [],
    city: null,
    state: null,
    postalCode: null,
    country: null,
    gstin: null,
    email: null,
    phone: null,
  };
}

/**
 * Reads an address out of the JSON blob an order carries.
 *
 * Order addresses are snapshots already, but they are `Json` — shape unenforced
 * and written by more than one code path over the years. Everything is read
 * defensively and anything unrecognised is dropped rather than printed raw.
 */
export function partyFromAddress(
  address: unknown,
  fallbackName: string,
  email: string | null
): PartySnapshot {
  const a = (address ?? {}) as Record<string, unknown>;
  const str = (key: string): string | null => {
    const value = a[key];
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  };

  const composed = [str('firstName'), str('lastName')].filter(Boolean).join(' ').trim();
  const name = str('name') ?? (composed || fallbackName);

  const lines = [str('line1') ?? str('address1') ?? str('street'), str('line2') ?? str('address2')]
    .filter((line): line is string => !!line);

  return {
    name,
    addressLines: lines,
    city: str('city') ?? str('locality'),
    state: str('state') ?? str('region'),
    postalCode: str('postalCode') ?? str('pincode') ?? str('zip'),
    country: str('country') ?? 'IN',
    gstin: str('gstin'),
    email: str('email') ?? email,
    phone: str('phone') ?? str('mobile'),
  };
}
